import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  GitBranch,
  History,
  LayoutTemplate,
  ListPlus,
  Plus,
  Save,
  Sigma,
  Trash2,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Modal,
  PageHeader,
  Spinner,
  Tabs,
  Textarea,
  useToast,
} from '../../ui';
import { useTemplates } from '../../features/templates/useTemplates';
import {
  blankField,
  blankOutput,
  blankTemplate,
  moveItem,
  validateTemplate,
} from '../../features/templates/templateModel';
import type {
  FieldDef,
  OutputDef,
  Template,
} from '../../features/templates/types';
import { FieldRow } from './FieldRow';
import { OutputRow } from './OutputRow';

type BuilderTab = 'fields' | 'outputs';

/**
 * Template Builder — creates a new template (/templates/new) or views/edits an
 * existing version (/templates/:id). Supports add/reorder/remove of fields and
 * outputs of any type, metadata editing, versioning (Save New Version) with
 * history, and (soft) delete. Pages call the useTemplates hook only.
 */
export function TemplateBuilderPage() {
  const params = useParams<{ id?: string }>();
  const id = params.id ? decodeURIComponent(params.id) : undefined;
  const mode: 'create' | 'edit' = id ? 'edit' : 'create';
  const navigate = useNavigate();
  const { toast } = useToast();

  const { summaries, getById, create, saveNewVersion, update, remove } =
    useTemplates();

  const [draft, setDraft] = useState<Template | null>(
    mode === 'create' ? blankTemplate('') : null,
  );
  const [loading, setLoading] = useState(mode === 'edit');
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<BuilderTab>('fields');
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Load the requested version in edit mode.
  useEffect(() => {
    let active = true;
    if (mode !== 'edit' || !id) return;
    setLoading(true);
    setNotFound(false);
    void getById(id)
      .then((tpl) => {
        if (!active) return;
        if (!tpl) {
          setNotFound(true);
        } else {
          setDraft(tpl);
        }
      })
      .catch(() => {
        if (active) setNotFound(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, mode, getById]);

  // Version history for the current name (from the lightweight summaries).
  const versions = useMemo(() => {
    if (!draft?.name) return [];
    return summaries
      .filter((s) => s.name === draft.name)
      .sort((a, b) => b.version - a.version);
  }, [summaries, draft?.name]);

  const patchDraft = useCallback((patch: Partial<Template>) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  // ---- Field operations -------------------------------------------------
  const addField = () =>
    setDraft((prev) =>
      prev ? { ...prev, fields: [...prev.fields, blankField()] } : prev,
    );
  const changeField = (index: number, patch: Partial<FieldDef>) =>
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            fields: prev.fields.map((f, i) =>
              i === index ? { ...f, ...patch } : f,
            ),
          }
        : prev,
    );
  const moveField = (index: number, delta: number) =>
    setDraft((prev) =>
      prev ? { ...prev, fields: moveItem(prev.fields, index, delta) } : prev,
    );
  const removeField = (index: number) =>
    setDraft((prev) =>
      prev ? { ...prev, fields: prev.fields.filter((_, i) => i !== index) } : prev,
    );

  // ---- Output operations ------------------------------------------------
  const outputs = draft?.outputs ?? [];
  const addOutput = () =>
    setDraft((prev) =>
      prev ? { ...prev, outputs: [...(prev.outputs ?? []), blankOutput()] } : prev,
    );
  const changeOutput = (index: number, patch: Partial<OutputDef>) =>
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            outputs: (prev.outputs ?? []).map((o, i) =>
              i === index ? { ...o, ...patch } : o,
            ),
          }
        : prev,
    );
  const moveOutput = (index: number, delta: number) =>
    setDraft((prev) =>
      prev
        ? { ...prev, outputs: moveItem(prev.outputs ?? [], index, delta) }
        : prev,
    );
  const removeOutput = (index: number) =>
    setDraft((prev) =>
      prev
        ? { ...prev, outputs: (prev.outputs ?? []).filter((_, i) => i !== index) }
        : prev,
    );

  // ---- Persistence ------------------------------------------------------
  const draftDto = (t: Template) => ({
    name: t.name.trim(),
    description: t.description,
    fields: t.fields,
    outputs: t.outputs,
  });

  const runSave = async (
    action: () => Promise<Template>,
    successMsg: string,
  ) => {
    if (!draft) return;
    const problems = validateTemplate(draft);
    if (problems.length > 0) {
      toast({ tone: 'error', title: 'Cannot save', description: problems[0] });
      return;
    }
    setSaving(true);
    try {
      const saved = await action();
      toast({ tone: 'success', title: successMsg, description: saved.id });
      navigate(`/templates/${encodeURIComponent(saved.id)}`);
      setDraft(saved);
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Save failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  const onSave = () => {
    if (!draft) return;
    if (mode === 'create') {
      void runSave(() => create(draftDto(draft)), 'Template created');
    } else {
      void runSave(() => update(draft), 'Template saved');
    }
  };

  const onSaveNewVersion = () => {
    if (!draft) return;
    void runSave(
      () => saveNewVersion(draftDto(draft)),
      'New version saved',
    );
  };

  const onConfirmDelete = async () => {
    if (!draft || mode !== 'edit') return;
    setSaving(true);
    try {
      await remove(draft.id);
      toast({ tone: 'success', title: 'Template deleted', description: draft.id });
      navigate('/templates');
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Delete failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
      setConfirmDelete(false);
    }
  };

  // ---- Render -----------------------------------------------------------
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner label="Loading template" />
      </div>
    );
  }

  if (notFound || !draft) {
    return (
      <div>
        <PageHeader
          icon={<LayoutTemplate className="h-5 w-5" />}
          title="Template not found"
          actions={
            <Button variant="outline" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate('/templates')}>
              Back to templates
            </Button>
          }
        />
        <EmptyState
          icon={<LayoutTemplate className="h-6 w-6" />}
          title="We couldn't load that template"
          description={id ? `No template exists with id "${id}".` : undefined}
        />
      </div>
    );
  }

  const tabItems = [
    {
      id: 'fields' as const,
      label: `Fields (${draft.fields.length})`,
      icon: <ListPlus className="h-4 w-4" />,
    },
    {
      id: 'outputs' as const,
      label: `Outputs (${outputs.length})`,
      icon: <Sigma className="h-4 w-4" />,
    },
  ];

  return (
    <div>
      <PageHeader
        icon={<LayoutTemplate className="h-5 w-5" />}
        title={mode === 'create' ? 'New Template' : draft.name || 'Edit Template'}
        description={
          mode === 'create'
            ? 'Define a fully-configurable estimation schema — no hardcoding.'
            : `Version ${draft.version}${draft.createdBy ? ` · by ${draft.createdBy}` : ''}`
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate('/templates')}
            >
              Back
            </Button>
            {mode === 'edit' && (
              <>
                <Button
                  variant="outline"
                  leftIcon={<GitBranch className="h-4 w-4" />}
                  loading={saving}
                  onClick={onSaveNewVersion}
                >
                  Save New Version
                </Button>
                <Button
                  variant="danger"
                  leftIcon={<Trash2 className="h-4 w-4" />}
                  disabled={saving}
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete
                </Button>
              </>
            )}
            <Button
              variant="primary"
              leftIcon={<Save className="h-4 w-4" />}
              loading={saving}
              onClick={onSave}
            >
              {mode === 'create' ? 'Create' : 'Save'}
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Metadata */}
          <Card>
            <CardHeader>
              <CardTitle>Template metadata</CardTitle>
              <Badge tone="accent">v{draft.version}</Badge>
            </CardHeader>
            <CardBody className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Name"
                value={draft.name}
                placeholder="Effort Estimation Model"
                hint={
                  mode === 'edit'
                    ? 'Changing the name will fork versions under the new name on next save.'
                    : 'Shared across all versions.'
                }
                onChange={(e) => patchDraft({ name: e.target.value })}
              />
              <Input
                label="Created by"
                value={draft.createdBy ?? ''}
                disabled
                onChange={() => undefined}
              />
              <Textarea
                label="Description"
                className="sm:col-span-2"
                value={draft.description ?? ''}
                placeholder="What does this template estimate?"
                onChange={(e) => patchDraft({ description: e.target.value })}
              />
            </CardBody>
          </Card>

          {/* Schema builder */}
          <Card>
            <CardHeader>
              <CardTitle>Schema</CardTitle>
              <Tabs
                items={tabItems}
                value={tab}
                onChange={(t) => setTab(t as BuilderTab)}
              />
            </CardHeader>
            <CardBody className="space-y-3">
              {tab === 'fields' ? (
                <>
                  {draft.fields.length === 0 ? (
                    <EmptyState
                      icon={<ListPlus className="h-6 w-6" />}
                      title="No fields yet"
                      description="Add configurable fields — base numbers, factors, multipliers, thresholds, enums, text or booleans."
                    />
                  ) : (
                    draft.fields.map((field, i) => (
                      <FieldRow
                        key={field.key + i}
                        field={field}
                        index={i}
                        total={draft.fields.length}
                        onChange={(patch) => changeField(i, patch)}
                        onMove={(delta) => moveField(i, delta)}
                        onRemove={() => removeField(i)}
                      />
                    ))
                  )}
                  <Button
                    variant="secondary"
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={addField}
                  >
                    Add field
                  </Button>
                </>
              ) : (
                <>
                  {outputs.length === 0 ? (
                    <EmptyState
                      icon={<Sigma className="h-6 w-6" />}
                      title="No derived outputs"
                      description="Optionally define formulas computed from field values, e.g. totalEffort = services * factor."
                    />
                  ) : (
                    outputs.map((output, i) => (
                      <OutputRow
                        key={output.key + i}
                        output={output}
                        index={i}
                        total={outputs.length}
                        onChange={(patch) => changeOutput(i, patch)}
                        onMove={(delta) => moveOutput(i, delta)}
                        onRemove={() => removeOutput(i)}
                      />
                    ))
                  )}
                  <Button
                    variant="secondary"
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={addOutput}
                  >
                    Add output
                  </Button>
                </>
              )}
            </CardBody>
          </Card>
        </div>

        {/* Version history */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>
                <span className="inline-flex items-center gap-2">
                  <History className="h-4 w-4 text-zinc-500" />
                  Version history
                </span>
              </CardTitle>
            </CardHeader>
            <CardBody className="space-y-2">
              {versions.length === 0 ? (
                <p className="text-sm text-zinc-500">
                  {mode === 'create'
                    ? 'History appears after the first save.'
                    : 'No other versions.'}
                </p>
              ) : (
                versions.map((v) => {
                  const current = v.id === draft.id;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() =>
                        navigate(`/templates/${encodeURIComponent(v.id)}`)
                      }
                      className={
                        'flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm transition-colors ' +
                        (current
                          ? 'border-accent/40 bg-accent-soft/40 text-zinc-100'
                          : 'border-surface-border bg-surface-1 text-zinc-300 hover:bg-surface-3')
                      }
                    >
                      <span className="inline-flex items-center gap-2">
                        <Badge tone={current ? 'accent' : 'neutral'}>
                          v{v.version}
                        </Badge>
                        <span className="text-xs text-zinc-500">
                          {v.fieldCount} fields
                        </span>
                      </span>
                      {current && <Badge tone="success">current</Badge>}
                    </button>
                  );
                })
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete this version?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button variant="danger" loading={saving} onClick={onConfirmDelete}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-zinc-300">
          This will remove version {draft.version} of{' '}
          <span className="font-medium text-zinc-100">{draft.name}</span>. Other
          versions are unaffected.
        </p>
      </Modal>
    </div>
  );
}

export default TemplateBuilderPage;
